import { useState, useEffect, useRef, Component, ReactNode } from 'react';
import './YouTubeEmbed.css';
import { fetchYouTubeVideos } from '../utils/youtubeCache';
import { BandcampEmbed } from './BandcampEmbed';
import { useMediaPlayerOptional } from '../contexts/MediaPlayerContext';

interface BandcampPreloadedEmbed {
  embedHtml: string;
  albumId?: string;
  title?: string;
}

interface YouTubeEmbedProps {
  query: string;
  artistName: string;
  trackName: string;
  onReady?: () => void;
  tryBandcamp?: boolean; // Enable Bandcamp fallback
  /** Pre-resolved YouTube video ID - skips search if provided */
  preloadedVideoId?: string;
  /** Pre-resolved Bandcamp embed data for fallback */
  preloadedBandcamp?: BandcampPreloadedEmbed;
}

// YouTube IFrame API types
declare global {
  interface Window {
    YT: {
      Player: new (elementId: string, options: YTPlayerOptions) => YTPlayer;
      PlayerState: {
        PLAYING: number;
        PAUSED: number;
        ENDED: number;
        BUFFERING: number;
      };
    };
    onYouTubeIframeAPIReady: () => void;
  }
}

interface YTPlayerOptions {
  videoId: string;
  playerVars?: {
    rel?: number;
    enablejsapi?: number;
    origin?: string;
  };
  events?: {
    onReady?: (event: { target: YTPlayer }) => void;
    onStateChange?: (event: { data: number; target: YTPlayer }) => void;
    onError?: (event: { data: number; target: YTPlayer }) => void;
  };
}

interface YTPlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  stopVideo: () => void;
  destroy: () => void;
  getPlayerState: () => number;
}

// Load YouTube IFrame API once
let ytApiLoaded = false;
let ytApiCallbacks: Array<() => void> = [];

function loadYouTubeApi(): Promise<void> {
  return new Promise((resolve) => {
    if (window.YT?.Player) {
      resolve();
      return;
    }

    ytApiCallbacks.push(resolve);

    if (!ytApiLoaded) {
      ytApiLoaded = true;
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = () => {
        ytApiCallbacks.forEach(cb => cb());
        ytApiCallbacks = [];
      };
    }
  });
}

// Error boundary to prevent embed errors from breaking the whole card
class EmbedErrorBoundary extends Component<{ children: ReactNode; fallback: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error) {
    console.error('[youtube] Embed error:', error);
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

function YouTubeEmbedInner({ query, artistName, trackName, onReady, tryBandcamp = true, preloadedVideoId, preloadedBandcamp }: YouTubeEmbedProps) {
  const [videoId, setVideoId] = useState<string | null>(preloadedVideoId || null);
  const [loading, setLoading] = useState(!preloadedVideoId);
  const [tryingBandcamp, setTryingBandcamp] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const [rebuilding, setRebuilding] = useState(false); // Currently rebuilding embed via API
  const [rebuildAttempted, setRebuildAttempted] = useState(false); // Already tried rebuild

  const playerRef = useRef<YTPlayer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const playerIdRef = useRef(`yt-player-${Math.random().toString(36).slice(2, 9)}`);

  const mediaPlayer = useMediaPlayerOptional();

  // Use preloaded video ID or fetch
  useEffect(() => {
    // If we have a preloaded video ID, use it directly
    if (preloadedVideoId) {
      setVideoId(preloadedVideoId);
      setLoading(false);
      onReady?.();
      return;
    }

    // Otherwise fetch
    setLoading(true);
    setTryingBandcamp(false);
    fetchYouTubeVideos(query, { artistName, trackName })
      .then(data => {
        if (data.videos && data.videos.length > 0) {
          setVideoId(data.videos[0].id);
        } else if (tryBandcamp) {
          setTryingBandcamp(true);
        }
        setLoading(false);
        onReady?.();
      })
      .catch(err => {
        console.error('[youtube] Search failed:', err);
        if (tryBandcamp) {
          setTryingBandcamp(true);
        }
        setLoading(false);
        onReady?.();
      });
  }, [query, artistName, trackName, tryBandcamp, preloadedVideoId]);

  // Load YouTube API
  useEffect(() => {
    if (videoId) {
      loadYouTubeApi().then(() => setApiReady(true));
    }
  }, [videoId]);

  // Store latest values in refs so event handler stays stable
  const mediaPlayerRef = useRef(mediaPlayer);
  const artistNameRef = useRef(artistName);
  const trackNameRef = useRef(trackName);

  useEffect(() => {
    mediaPlayerRef.current = mediaPlayer;
  }, [mediaPlayer]);

  useEffect(() => {
    artistNameRef.current = artistName;
    trackNameRef.current = trackName;
  }, [artistName, trackName]);

  // Initialize YouTube player - only depends on apiReady and videoId
  useEffect(() => {
    if (!apiReady || !videoId || !containerRef.current) return;

    // Clear container first
    containerRef.current.innerHTML = '';

    // Create player element
    const playerId = playerIdRef.current;
    const playerDiv = document.createElement('div');
    playerDiv.id = playerId;
    containerRef.current.appendChild(playerDiv);

    playerRef.current = new window.YT.Player(playerId, {
      videoId,
      playerVars: {
        rel: 0,
        enablejsapi: 1,
        origin: window.location.origin,
      },
      events: {
        onStateChange: (event: { data: number }) => {
          const mp = mediaPlayerRef.current;
          if (!mp) return;

          const title = `${artistNameRef.current} - ${trackNameRef.current}`;

          if (event.data === window.YT.PlayerState.PLAYING) {
            mp.registerPlaying({
              type: 'youtube',
              id: videoId,
              title,
              artistName: artistNameRef.current,
            });
          } else if (event.data === window.YT.PlayerState.PAUSED) {
            mp.setPlayState(videoId, false);
          } else if (event.data === window.YT.PlayerState.ENDED) {
            mp.registerStopped(videoId);
          }
        },
        onError: async (event: { data: number }) => {
          // Error codes: 101, 150 = embedding disabled by owner
          // 100 = video not found, 2 = invalid param, 5 = HTML5 error
          console.warn(`[youtube] Player error for ${videoId}: code ${event.data}`);

          if ((event.data === 101 || event.data === 150 || event.data === 100) && !rebuildAttempted) {
            // Embedding disabled - try to rebuild via API
            console.log(`[youtube] Attempting to rebuild embed for "${artistName}"...`);
            setRebuilding(true);
            setRebuildAttempted(true);

            try {
              const response = await fetch('/api/v1/research/rebuild-embed', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ artistName, videoId }),
              });

              if (response.ok) {
                const data = await response.json();
                if (data.youtubeVideoId && data.youtubeVideoId !== videoId) {
                  console.log(`[youtube] Got new video ID: ${data.youtubeVideoId}`);
                  // Destroy current player and set new video ID
                  if (playerRef.current) {
                    try { playerRef.current.destroy(); } catch {}
                    playerRef.current = null;
                  }
                  setVideoId(data.youtubeVideoId);
                  setRebuilding(false);
                  return;
                }
              }
            } catch (err) {
              console.error('[youtube] Rebuild failed:', err);
            }

            // Rebuild failed or no new video - fall back to Bandcamp
            setRebuilding(false);
            setTryingBandcamp(true);
          }
        },
      },
    });

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          // Ignore destroy errors
        }
        playerRef.current = null;
      }
    };
  }, [apiReady, videoId]);

  // Track current videoId for unmount cleanup
  const currentVideoIdRef = useRef<string | null>(null);
  currentVideoIdRef.current = videoId;

  // Register controls with media player context
  useEffect(() => {
    if (!mediaPlayer || !videoId) return;

    const controls = {
      pause: () => playerRef.current?.pauseVideo(),
      play: () => playerRef.current?.playVideo(),
      stop: () => {
        playerRef.current?.stopVideo();
        mediaPlayer.registerStopped(videoId);
      },
    };

    mediaPlayer.registerControls(videoId, controls);

    return () => {
      mediaPlayer.unregisterControls(videoId);
    };
  }, [mediaPlayer, videoId]);

  // Clear playback state when component unmounts
  useEffect(() => {
    return () => {
      // Use refs to get current values at unmount time
      const mp = mediaPlayerRef.current;
      const vid = currentVideoIdRef.current;
      if (mp && vid) {
        mp.registerStopped(vid);
      }
    };
  }, []); // Empty deps = only runs on unmount

  const youtubeSearchUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`;

  // Show rebuilding state
  if (rebuilding) {
    return (
      <div className="youtube-embed loading">
        <div className="embed-placeholder">
          <div className="play-icon">⟳</div>
          <div className="track-info">
            <span className="track-name">{trackName}</span>
            <span className="artist-name">{artistName}</span>
          </div>
          <span className="search-hint">Finding embeddable video...</span>
        </div>
      </div>
    );
  }

  // Show YouTube player when we have a videoId and API is ready
  if (videoId && apiReady) {
    return (
      <div className="youtube-embed ready" ref={containerRef} />
    );
  }

  // Show loading state while API loads
  if (videoId && !apiReady) {
    return (
      <div className="youtube-embed loading">
        <div className="embed-placeholder">
          <div className="play-icon">▶</div>
          <div className="track-info">
            <span className="track-name">{trackName}</span>
            <span className="artist-name">{artistName}</span>
          </div>
          <span className="search-hint">Loading player...</span>
        </div>
      </div>
    );
  }

  // Try Bandcamp if YouTube failed
  if (tryingBandcamp) {
    return (
      <BandcampEmbed
        artistName={artistName}
        trackName={trackName}
        preloadedEmbed={preloadedBandcamp}
        onReady={onReady}
      />
    );
  }

  // Fallback - show link to YouTube search
  return (
    <a
      href={youtubeSearchUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`youtube-embed fallback ${loading ? 'loading' : ''}`}
    >
      <div className="embed-placeholder">
        <div className="play-icon">▶</div>
        <div className="track-info">
          <span className="track-name">{trackName}</span>
          <span className="artist-name">{artistName}</span>
        </div>
        {loading ? (
          <span className="search-hint">Loading...</span>
        ) : (
          <span className="search-hint">Couldn't find video — tap to search YouTube</span>
        )}
      </div>
    </a>
  );
}

export function YouTubeEmbed(props: YouTubeEmbedProps) {
  const fallback = (
    <a
      href={`https://www.youtube.com/results?search_query=${encodeURIComponent(props.query)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="youtube-embed fallback"
    >
      <div className="embed-placeholder">
        <div className="play-icon">▶</div>
        <div className="track-info">
          <span className="track-name">{props.trackName}</span>
          <span className="artist-name">{props.artistName}</span>
        </div>
        <span className="search-hint">Couldn't find video — tap to search YouTube</span>
      </div>
    </a>
  );

  return (
    <EmbedErrorBoundary fallback={fallback}>
      <YouTubeEmbedInner {...props} />
    </EmbedErrorBoundary>
  );
}
